// components/scheduler/DynamicFilterForm.tsx
import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, DatePicker, Select, Switch, Divider, Spin } from 'antd';
import { FilterOptionDTO } from "@/shared/api/model";
import { getFilterOptions } from "@/shared/api/client/notification-process-service-controller";

const { Option } = Select;

interface DynamicFilterFormProps {
    serviceName: string | null;
    value: Record<string, any>;
    onChange: (filters: Record<string, any>) => void;
}

const DynamicFilterForm: React.FC<DynamicFilterFormProps> = ({
                                                                 serviceName,
                                                                 value,
                                                                 onChange
                                                             }) => {
    const [filterOptions, setFilterOptions] = useState<FilterOptionDTO[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!serviceName) {
            setFilterOptions([]);
            return;
        }

        const loadFilters = async () => {
            setIsLoading(true);
            try {
                const response = await getFilterOptions(serviceName);
                setFilterOptions(response);
            } catch (error) {
                console.error('Error loading filter options', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadFilters();
    }, [serviceName]);

    const renderFilterInput = (option: FilterOptionDTO) => {
        const { name, type, values: optionValues } = option;

        switch (type) {
            case 'text':
                return <Input />;

            case 'number':
                return <InputNumber style={{ width: '100%' }} />;

            case 'date':
                return <DatePicker style={{ width: '100%' }} />;

            case 'select':
                return (
                    <Select>
                        {optionValues?.map(value => (
                            <Option key={value.value} value={value.value}>
                                {value.label}
                            </Option>
                        ))}
                    </Select>
                );

            case 'boolean':
                return <Switch />;

            default:
                return <Input />;
        }
    };

    // Handle form field changes
    const handleFieldChange = (changedValues: any, allValues: any) => {
        onChange(allValues.filters || {});
    };

    if (!serviceName) {
        return null;
    }

    return (
        <>
            <Divider orientation="left">Notification Filters</Divider>

            {isLoading ? (
                <div className="text-center py-4">
                    <Spin />
                    <div className="mt-2">Loading filter options...</div>
                </div>
            ) : filterOptions.length === 0 ? (
                <div className="text-center py-4">No filter options available for this notification type</div>
            ) : (
                <Form.Item name="filters" initialValue={value} noStyle>
                    <Form
                        layout="vertical"
                        onValuesChange={handleFieldChange}
                        initialValues={{ filters: value }}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filterOptions.map(option => (
                                <Form.Item
                                    key={option.name}
                                    // @ts-ignore
                                    name={['filters', option.name]}
                                    label={option.displayName}
                                    tooltip={option.description}
                                    valuePropName={option.type === 'boolean' ? 'checked' : 'value'}
                                >
                                    {renderFilterInput(option)}
                                </Form.Item>
                            ))}
                        </div>
                    </Form>
                </Form.Item>
            )}
        </>
    );
};

export default DynamicFilterForm;